import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trophy, Clock, Coins, Gamepad2, Share2 } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { getVisitorId } from "../utils/analytics";
import CafeCardShare from "../components/CafeCardShare";
import WeeklyResetParty from "../components/WeeklyResetParty";
import usePageMeta from "../hooks/usePageMeta";

const BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDuration = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const rankColor = (rank) => {
  if (rank === 1) return { bg: "#FFB400", label: "🥇" };
  if (rank === 2) return { bg: "#C0C7CE", label: "🥈" };
  if (rank === 3) return { bg: "#D89365", label: "🥉" };
  return { bg: "#FFF9E6", label: `#${rank}` };
};

// Convert a 2-letter country code to its flag emoji (regional indicator pair).
export const flagOf = (cc) => {
  const code = (cc || "").toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "🌍";
  return String.fromCodePoint(
    ...[...code].map((ch) => 127397 + ch.charCodeAt(0))
  );
};

const fmtDurationShort = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const SORTS = [
  { key: "playtime", label: "Time played", shortLabel: "Time", icon: Clock, color: "#5F27CD" },
  { key: "coins", label: "Coins earned", shortLabel: "Coins", icon: Coins, color: "#FFB400" },
  { key: "plays", label: "Games played", shortLabel: "Games", icon: Gamepad2, color: "#00A699" },
];

// Public leaderboard. Every kid can see the real ranks; names are trimmed
// to their first 3 letters on the server so nobody is doxxed. Their own
// row is highlighted with a "you" pill so they know exactly where they stand.
const Leaderboard = () => {
  usePageMeta("Leaderboard · 67Cafe", "See who's got the biggest streak, the tallest coin stack, and the most game time at 67Cafe.");
  const { avatar } = useStore();
  const [sort, setSort] = useState("playtime");
  const [data, setData] = useState({ rows: [], me: null, total_ranked: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr("");
    const vid = getVisitorId();
    fetch(`${BASE}/api/leaderboard?sort=${sort}&limit=20&visitor_id=${encodeURIComponent(vid)}`)
      .then((r) => r.json())
      .then((j) => { if (!cancelled) setData(j); })
      .catch(() => { if (!cancelled) setErr("Could not load the leaderboard."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sort]);

  const valueFor = (row) => {
    if (sort === "playtime") return fmtDuration(row.playtime_seconds);
    if (sort === "coins") return `🪙 ${row.coins_earned}`;
    return `🎮 ${row.total_plays}`;
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6" data-testid="leaderboard-page">
      <Link
        to="/shop"
        className="btn-3d btn-sm btn-ghost mb-4"
        data-testid="leaderboard-back"
      >
        <ArrowLeft size={18} strokeWidth={3} /> Back
      </Link>

      <div
        className="chunky-card p-5 sm:p-6 mb-6 flex items-center gap-4"
        style={{ background: "#FFB400" }}
      >
        <Trophy size={40} strokeWidth={3} color="#2b2b2b" />
        <div>
          <h1 className="font-display text-2xl sm:text-3xl leading-none" data-testid="leaderboard-title">
            The 67Cafe Leaderboard
          </h1>
          <p className="text-sm sm:text-base mt-1" style={{ color: "#4a3200" }}>
            Real ranks, first-3-letters only. {data.total_ranked || 0} kids on the board.
          </p>
        </div>
      </div>

      {/* Sort tabs — 3-column grid so nothing scrolls off mobile screens.
          Icons hide below 400px so the short labels always stay readable. */}
      <WeeklyResetParty />
      <div className="grid grid-cols-3 gap-2 mb-4" data-testid="leaderboard-sort">
        {SORTS.map(({ key, label, shortLabel, icon: Icon, color }) => (
          <button
            key={key}
            onClick={() => setSort(key)}
            data-testid={`leaderboard-sort-${key}`}
            aria-pressed={sort === key}
            className="btn-3d btn-sm w-full flex items-center justify-center gap-1 px-1 sm:px-2"
            style={{
              background: sort === key ? color : "#fff",
              color: sort === key ? "#fff" : "#2b2b2b",
              opacity: sort === key ? 1 : 0.9,
              minWidth: 0,
            }}
          >
            <Icon size={14} strokeWidth={3} className="hidden sm:inline-block" />
            <span className="sm:hidden">{shortLabel}</span>
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* My rank */}
      {data.me && (
        <div
          className="chunky-card p-3 mb-4 flex items-center gap-3"
          style={{ background: "#EAF6FF" }}
          data-testid="leaderboard-me"
        >
          <div
            className="rounded-full flex items-center justify-center font-display shrink-0"
            style={{
              width: 44, height: 44, fontSize: 14,
              background: "#54A0FF", color: "#fff",
              border: "3px solid #2b2b2b", boxShadow: "0 3px 0 #2b2b2b",
            }}
          >
            #{data.me.rank}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg leading-none truncate">
              <span style={{ marginRight: 6 }}>{avatar || data.me.avatar || "🐻"}</span>
              You ({data.me.name})
            </div>
            <div className="text-xs flex items-center gap-1" style={{ color: "#484848" }}>
              <span aria-hidden="true" style={{ fontSize: 14 }} data-testid="leaderboard-me-flag">
                {flagOf(data.me.country_code)}
              </span>
              <span className="truncate">{valueFor(data.me)}</span>
            </div>
          </div>
          <button
            onClick={() => setShareOpen(true)}
            className="btn-3d btn-sm shrink-0"
            data-testid="leaderboard-share-btn"
            style={{ background: "#54A0FF", color: "#fff" }}
            aria-label="Share my rank"
          >
            <Share2 size={16} strokeWidth={3} />
            <span className="hidden sm:inline">Share</span>
          </button>
        </div>
      )}

      {/* Top rows */}
      {loading && (
        <div className="chunky-card p-6 text-center font-display" data-testid="leaderboard-loading">
          Loading…
        </div>
      )}
      {!loading && err && (
        <div className="chunky-card p-6 text-center font-display" data-testid="leaderboard-error" style={{ background: "#FFEBED" }}>
          {err}
        </div>
      )}
      {!loading && !err && data.rows.length === 0 && (
        <div className="chunky-card p-6 text-center font-display" data-testid="leaderboard-empty">
          No ranks yet — be the first to play!
        </div>
      )}
      {!loading && !err && data.rows.length > 0 && (
        <ol className="space-y-2" data-testid="leaderboard-rows">
          {data.rows.map((row) => {
            const rc = rankColor(row.rank);
            return (
              <li
                key={row.visitor_id}
                data-testid={`leaderboard-row-${row.rank}`}
                className="chunky-card p-3 flex items-center gap-3"
                style={row.is_me ? { background: "#EAF6FF" } : {}}
              >
                <div
                  className="rounded-full flex items-center justify-center font-display shrink-0"
                  style={{
                    width: 44, height: 44, fontSize: row.rank <= 3 ? 22 : 16,
                    lineHeight: 1,
                    background: rc.bg,
                    border: "3px solid #2b2b2b", boxShadow: "0 3px 0 #2b2b2b",
                  }}
                >
                  {rc.label}
                </div>
                <div
                  className="rounded-full flex items-center justify-center shrink-0"
                  style={{
                    width: 40, height: 40, fontSize: 22, background: "#fff",
                    border: "3px solid #2b2b2b", boxShadow: "0 3px 0 #2b2b2b",
                  }}
                  aria-hidden="true"
                >
                  {row.avatar || "🐻"}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="font-display text-lg leading-tight truncate"
                    data-testid={`leaderboard-row-${row.rank}-name`}
                  >
                    {row.name}
                    {row.is_me && (
                      <span
                        className="ml-2 rounded-full px-2 py-0.5 text-xs align-middle"
                        style={{ background: "#54A0FF", color: "#fff", border: "2px solid #2b2b2b" }}
                      >
                        YOU
                      </span>
                    )}
                  </div>
                  <div className="text-xs flex items-center gap-1" style={{ color: "#484848" }}>
                    <span
                      aria-hidden="true"
                      style={{ fontSize: 14 }}
                      data-testid={`leaderboard-row-${row.rank}-flag`}
                    >
                      {flagOf(row.country_code)}
                    </span>
                    <span className="truncate">{row.country || "somewhere fun"}</span>
                  </div>
                </div>
                <div
                  className="font-display text-base sm:text-lg text-right shrink-0"
                  data-testid={`leaderboard-row-${row.rank}-value`}
                >
                  {valueFor(row)}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <p className="text-xs mt-4 text-center" style={{ color: "#484848" }}>
        Only the first 3 letters of each nickname are shown to keep kids safe.
      </p>

      <CafeCardShare
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        row={data.me ? { ...data.me, avatar: avatar || data.me.avatar } : null}
        sortLabel={SORTS.find((s) => s.key === sort)?.label || ""}
        sortValue={data.me ? valueFor(data.me) : ""}
      />
    </div>
  );
};

export default Leaderboard;
